import { useState, useEffect } from 'react'
import { supabase } from '../config/supabase'
import toast from 'react-hot-toast'

export const useHomepageContent = () => {
  const [content, setContent] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchContent()
    
    // Subscribe to content changes
    const subscription = supabase
      .channel('homepage-content-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'homepage_content' 
      }, () => fetchContent())
      .subscribe()

    return () => subscription.unsubscribe()
  }, [])

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('homepage_content')
        .select('*')

      if (error) throw error

      // Convert array to object with section as key
      const contentObj = {}
      data?.forEach(item => {
        contentObj[item.section] = item.content
      })
      
      setContent(contentObj)
    } catch (error) {
      console.error('Error fetching homepage content:', error)
      // Set default content if fetch fails
      setContent(getDefaultContent())
    } finally {
      setLoading(false)
    }
  }

  const updateContent = async (section, newContent) => {
    try {
      const { error } = await supabase
        .from('homepage_content')
        .upsert({
          section,
          content: newContent
        })

      if (error) throw error
      
      toast.success('Content updated successfully')
      return { error: null }
    } catch (error) {
      console.error('Error updating content:', error)
      toast.error('Failed to update content')
      return { error }
    }
  }

  const getDefaultContent = () => ({
    hero: {
      headline: "Sustainable Weight Loss for LIFE",
      subheadline: "Simple, sustainable habits for people with real lives.",
      primaryCTA: { text: "Explore Courses", link: "/courses" },
      secondaryCTA: { text: "Book a Free Call", link: "https://tidycal.com/empowernutrition/30-minute-consultation" }
    },
    howItWorks: {
      title: "How It Works",
      subtitle: "Our proven 3-step approach to lasting transformation",
      steps: [
        {
          title: "PPC Method",
          description: "Protein, Portions, and Consistency - the foundation of sustainable nutrition",
          icon: "target"
        },
        {
          title: "Real-World Coaching", 
          description: "Practical strategies that fit your busy lifestyle and real challenges",
          icon: "users"
        },
        {
          title: "Data-Informed Progress",
          description: "Track what matters and make adjustments based on your results", 
          icon: "trendingUp"
        }
      ]
    },
    testimonials: {
      title: "Real Results from Real People",
      testimonials: [
        {
          name: "Sarah M.",
          result: "Lost 35 lbs in 6 months",
          quote: "Finally found an approach that works with my crazy schedule. No more yo-yo dieting!",
          image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face"
        },
        {
          name: "Mike R.", 
          result: "Down 28 lbs, kept it off 2 years",
          quote: "The PPC method is so simple but incredibly effective. This changed my relationship with food.",
          image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
        },
        {
          name: "Jennifer K.",
          result: "Lost 42 lbs, gained confidence", 
          quote: "I love that this focuses on habits, not restriction. I actually enjoy eating healthy now!",
          image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
        }
      ]
    },
    newsletter: {
      title: "No Bull-Shit Nutrition",
      subtitle: "Get weekly evidence-based nutrition tips that actually work in the real world",
      placeholder: "Enter your email address",
      buttonText: "Get Free Tips"
    },
    faq: {
      title: "Frequently Asked Questions",
      questions: [
        {
          question: "How is this different from other diet programs?",
          answer: "We focus on sustainable habits, not quick fixes. Our PPC method teaches you principles you can use for life, not restrictive rules that fail when life gets busy."
        },
        {
          question: "Do I need to count calories or track macros?",
          answer: "Not necessarily. We teach portion awareness and intuitive eating principles. Some people find tracking helpful initially, but our goal is to help you develop natural awareness."
        },
        {
          question: "How quickly will I see results?",
          answer: "Most people see initial changes within 2-3 weeks, but sustainable weight loss happens at 1-2 lbs per week. We focus on building habits that last, not quick drops that rebound."
        },
        {
          question: "What if I have dietary restrictions or medical conditions?",
          answer: "Our approach works with most dietary needs. We always recommend consulting with your healthcare provider, and our methods can be adapted for various restrictions and conditions."
        },
        {
          question: "Is there ongoing support after I complete a course?",
          answer: "Yes! Course graduates get access to our private community, monthly Q&A calls, and ongoing resources to help maintain your progress long-term."
        }
      ]
    }
  })

  return {
    content,
    loading,
    updateContent,
    refetch: fetchContent
  }
}